import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Tooltip, Button, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Divider, Switch, FormControlLabel, Grid
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Groups2, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useBankAccounts } from '../hooks/useBankAccounts';
import { useGLAccounts }  from '../hooks/useGLAccounts';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const FREQUENCIES = ['Monthly', 'Bi-Weekly', 'Weekly', 'Daily'];
const FREQ_COLOR  = { Monthly: 'primary', 'Bi-Weekly': 'info', Weekly: 'warning', Daily: 'error' };

const emptyForm = {
  groupCode: '', name: '', frequency: 'Monthly',
  glAccount: '', bankAccountNo: '', description: '', isActive: true
};

const PayGroupList = () => {
  const navigate = useNavigate();
  const { banks }    = useBankAccounts();
  const { accounts } = useGLAccounts();

  const [groups,   setGroups]   = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open, value: form, onClose: () => setOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [confirmDlg, setConfirmDlg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/employees/pay-groups/all`, { headers: hdrs() });
      setGroups(data);
    } catch { setError('Failed to load pay groups'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setFormErr(''); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      groupCode: g.groupCode, name: g.name, frequency: g.frequency || 'Monthly',
      glAccount: g.glAccount || '', bankAccountNo: g.bankAccountNo || '',
      description: g.description || '', isActive: g.isActive !== false
    });
    setFormErr(''); setOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.groupCode?.trim()) { setFormErr('Group code is required'); return; }
    if (!form.name?.trim())      { setFormErr('Name is required'); return; }
    setSaving(true); setFormErr('');
    try {
      if (editing) {
        await axios.put(`${API}/api/employees/pay-groups/${editing.groupCode}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/employees/pay-groups`, form, { headers: hdrs() });
      }
      setOpen(false);
      setSuccess(editing ? 'Updated' : 'Created');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setFormErr(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const confirmAction = async () => {
    const g = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/employees/pay-groups/${g.groupCode}`, { headers: hdrs() });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const bankLabel = (accountNo) => {
    if (!accountNo) return '—';
    const b = banks.find(b => b.accountNo === accountNo);
    return b ? `${b.bankName} (${b.accountNo})` : accountNo;
  };

  const glLabel = (accountNo) => {
    if (!accountNo) return '—';
    const a = accounts.find(a => a.accountNo === accountNo);
    return a ? `${a.accountNo} · ${a.accountName}` : accountNo;
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr')}>Back</Button>
              <Groups2 sx={{ color: '#546e7a' }} />
              <Typography variant="h5" fontWeight={700} color="#37474f">Pay Groups</Typography>
              <Chip label={`${groups.filter(g => g.isActive !== false).length} active`} size="small" color="success" />
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
              Add Pay Group
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell align="center"><strong>Frequency</strong></TableCell>
                <TableCell><strong>Salary GL</strong></TableCell>
                <TableCell><strong>Payment Bank</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No pay groups. Defaults are seeded automatically on first use.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : groups.map(g => (
                <TableRow key={g.groupCode} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Chip label={g.groupCode} size="small" variant="outlined"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}
                      color={g.isActive === false ? 'text.disabled' : 'inherit'}>
                      {g.name}
                    </Typography>
                    {g.description && (
                      <Typography variant="caption" color="text.secondary">{g.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={g.frequency || 'Monthly'} size="small"
                      color={FREQ_COLOR[g.frequency] || 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{glLabel(g.glAccount)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{bankLabel(g.bankAccountNo)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={g.isActive !== false ? 'Active' : 'Inactive'} size="small"
                      color={g.isActive !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(g)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDlg(g)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirm Delete Dialog */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Pay Group
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete pay group <strong>{confirmDlg?.name}</strong> ({confirmDlg?.groupCode})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog ── */}
      {discardDialog}
      <Dialog open={open} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.name}` : 'Add Pay Group'}</DialogTitle>
        <DialogContent dividers>
          {formErr && <Alert severity="error" sx={{ mb: 2 }}>{formErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Group Code *" name="groupCode" value={form.groupCode}
                onChange={e => setForm(f => ({ ...f, groupCode: e.target.value.toUpperCase() }))}
                size="small" fullWidth disabled={!!editing}
                helperText={editing ? 'Cannot change' : 'e.g. MONTHLY, CASUAL'}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} size="small" fullWidth
                placeholder="e.g. Teachers Payroll" />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Frequency" name="frequency" value={form.frequency}
                onChange={handle} size="small" fullWidth>
                {FREQUENCIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Salary Expense GL" name="glAccount" value={form.glAccount}
                onChange={handle} size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
                helperText="GL account debited on payroll run (Expense type recommended)">
                {glAccountOptions(accounts)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Payment Bank Account" name="bankAccountNo"
                value={form.bankAccountNo} onChange={handle} size="small" fullWidth
                helperText="Bank account from which salaries are paid">
                <MenuItem value=""><em>— Not set —</em></MenuItem>
                {banks.map(b => (
                  <MenuItem key={b.accountNo} value={b.accountNo}>
                    {b.bankName} — {b.accountName} ({b.accountNo})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" name="description" value={form.description}
                onChange={handle} size="small" fullWidth multiline rows={2} />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.isActive}
                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                  label="Active" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayGroupList;
